import {
  IInitEventDisruptionProtocolHeader,
  IInitEventDisruptionPvsTask,
  IInitEventDisruptionLinkChild,
  IInitEventDisruptionTag,
  EInitEventDisruptionProtocolStepStatusCode,
} from 'ngx-mt-ui-lib/api-client';
import { createReducer, on } from '@ngrx/store';
import { EStateStatus } from '@app/app.state';
import { compareObjects } from 'mt-ui-lib/functions';
import { IApiError } from '@app/core/interfaces/error.interface';
import { HttpErrorResponse } from '@angular/common/http';
import {
  IDisruptionProtocolStepExtended,
  IDisruptionTrackClosureItem,
  IHistDisruptionProtocol,
} from '@app/core/interfaces/disruption.interface';
import { DisruptionActions } from './disruption.actions';
import { ISelectItem } from 'mt-ui-lib/interfaces';

import dayjs from 'dayjs';
import { IDisruptionCardInfo, toDisruptionCardInfo } from './disruption.types';

export type { IDisruptionCardInfo } from './disruption.types';

export interface IDisruptionState {
  id: number | null;
  item: IDisruptionCardInfo | null;
  form: Partial<IDisruptionCardInfo> | null;
  tags: IInitEventDisruptionTag[];
  activeProtocol: IInitEventDisruptionProtocolHeader;
  activeProtocolActualDate: string | null;
  activeProtocolSteps: IDisruptionProtocolStepExtended[];
  histProtocols: IHistDisruptionProtocol[];
  pvsTaskList: IInitEventDisruptionPvsTask[];
  childDisruptionList: IInitEventDisruptionLinkChild[];
  metroTrackClosureList: IDisruptionTrackClosureItem[];
  pendingItem: IDisruptionCardInfo | null;
  remoteInfoChanged: boolean;
  infoRevision: number;
  hasChanges: boolean;
  stateListWithCount: ISelectItem<{ count: number }, string>[];
  status: EStateStatus;
  error: HttpErrorResponse & { error: IApiError };
}
export const initialState: IDisruptionState = {
  id: null,
  item: null,
  form: null,
  tags: [],
  activeProtocol: null,
  activeProtocolActualDate: null,
  activeProtocolSteps: [],
  histProtocols: [],
  pvsTaskList: [],
  childDisruptionList: [],
  metroTrackClosureList: [],
  pendingItem: null,
  remoteInfoChanged: false,
  infoRevision: 0,
  hasChanges: false,
  stateListWithCount: [],
  status: EStateStatus.PENDING,
  error: null,
};

export const DisruptionReducer = createReducer(
  initialState,
  on(DisruptionActions.selectItem, ({ stateListWithCount }, { id }) => ({
    ...initialState,
    stateListWithCount,
    id,
  })),
  on(
    DisruptionActions.requestItemInfo,
    DisruptionActions.requestCreateItem,
    DisruptionActions.requestUpdateItem,
    DisruptionActions.requestAddPvs,
    DisruptionActions.requestAddParentDisruption,
    DisruptionActions.requestAddParentSecurityEvent,
    DisruptionActions.requestAddChildDisruption,
    DisruptionActions.requestDeleteChildDisruption,
    DisruptionActions.requestSetToWorking,
    DisruptionActions.requestSetToCompleted,
    DisruptionActions.requestSetToClose,
    DisruptionActions.requestSetToCancel,
    DisruptionActions.requestSetToArchive,
    DisruptionActions.requestFixCompensationRoute,
    DisruptionActions.requestRunProtocol,
    DisruptionActions.requestCancelProtocol,
    DisruptionActions.requestAddProtocolStep,
    DisruptionActions.requestProtocolStepsDelete,
    DisruptionActions.requestProtocolStepsComplete,
    DisruptionActions.requestProtocolStepsCancel,
    DisruptionActions.requestEditProtocolStepReactRole,
    DisruptionActions.requestEditProtocolStepManualFinishedDate,
    DisruptionActions.requestEditProtocolStepExecuteResult,
    DisruptionActions.requestChildDisruptionList,
    DisruptionActions.requestTrackClosureList,
    DisruptionActions.requestShowOnWall,
    DisruptionActions.requestHideFromWall,
    DisruptionActions.requestAddTrackClosure,
    DisruptionActions.requestCompleteTrackClosure,
    DisruptionActions.requestTags,
    DisruptionActions.requestEditTags,
    DisruptionActions.requestDeleteSystemTag,
    (state) => ({
      ...state,
      status: EStateStatus.LOADING,
    }),
  ),
  on(DisruptionActions.requestProtocols, (state, { silent }) =>
    silent
      ? state
      : {
          ...state,
          status: EStateStatus.LOADING,
        },
  ),
  on(DisruptionActions.requestItemInfoSuccess, (state, payload) => {
    if (state.id != null && payload.item?.id !== state.id) {
      return { ...state, status: EStateStatus.SUCCESS };
    }

    if (isStaleInfoResponse(state.infoRevision, payload.observedRevision)) {
      return { ...state, status: EStateStatus.SUCCESS };
    }

    return {
      ...state,
      item: toDisruptionCardInfo(payload.item),
      pendingItem: null,
      remoteInfoChanged: false,
      status: EStateStatus.SUCCESS,
    };
  }),
  on(DisruptionActions.requestCreateItemSuccess, (state, payload) => ({
    ...state,
    id: payload.id,
    hasChanges: false,
    status: EStateStatus.SUCCESS,
  })),
  on(DisruptionActions.requestUpdateItemSuccess, (state, payload) => ({
    ...state,
    item: toDisruptionCardInfo(payload.item),
    pendingItem: null,
    remoteInfoChanged: false,
    hasChanges: false,
    status: EStateStatus.SUCCESS,
  })),
  on(
    DisruptionActions.requestAddPvsSuccess,
    DisruptionActions.requestAddParentDisruptionSuccess,
    DisruptionActions.requestAddChildDisruptionSuccess,
    DisruptionActions.requestDeleteChildDisruptionSuccess,
    DisruptionActions.requestAddParentSecurityEventSuccess,
    DisruptionActions.requestSetToWorkingSuccess,
    DisruptionActions.requestSetToCompletedSuccess,
    DisruptionActions.requestSetToCloseSuccess,
    DisruptionActions.requestSetToCancelSuccess,
    DisruptionActions.requestSetToArchiveSuccess,
    DisruptionActions.requestFixCompensationRouteSuccess,
    DisruptionActions.requestRunProtocolSuccess,
    DisruptionActions.requestAddProtocolStepSuccess,
    DisruptionActions.requestProtocolStepsDeleteSuccess,
    DisruptionActions.requestProtocolStepsCompleteSuccess,
    DisruptionActions.requestProtocolStepsCancelSuccess,
    DisruptionActions.requestEditProtocolStepReactRoleSuccess,
    DisruptionActions.requestEditProtocolStepManualFinishedDateSuccess,
    DisruptionActions.requestEditProtocolStepExecuteResultSuccess,
    DisruptionActions.requestChildDisruptionListSuccess,
    DisruptionActions.requestTrackClosureListSuccess,
    DisruptionActions.requestShowOnWallSuccess,
    DisruptionActions.requestHideFromWallSuccess,
    DisruptionActions.requestAddTrackClosureSuccess,
    DisruptionActions.requestCompleteTrackClosureSuccess,
    DisruptionActions.requestEditTagsSuccess,
    DisruptionActions.requestDeleteSystemTagSuccess,
    (state) => ({
      ...state,
      hasChanges: false,
      status: EStateStatus.SUCCESS,
    }),
  ),
  on(DisruptionActions.requestCancelProtocolSuccess, (state) => ({
    ...state,
    hasChanges: false,
    activeProtocol: null,
    activeProtocolActualDate: null,
    activeProtocolSteps: [],
    status: EStateStatus.SUCCESS,
  })),
  on(
    DisruptionActions.requestItemInfoError,
    DisruptionActions.requestCreateItemError,
    DisruptionActions.requestUpdateItemError,
    DisruptionActions.requestAddPvsError,
    DisruptionActions.requestAddParentDisruptionError,
    DisruptionActions.requestAddParentSecurityEventError,
    DisruptionActions.requestAddChildDisruptionError,
    DisruptionActions.requestDeleteChildDisruptionError,
    DisruptionActions.requestSetToWorkingError,
    DisruptionActions.requestSetToCompletedError,
    DisruptionActions.requestSetToCloseError,
    DisruptionActions.requestSetToCancelError,
    DisruptionActions.requestSetToArchiveError,
    DisruptionActions.requestFixCompensationRouteError,
    DisruptionActions.requestRunProtocolError,
    DisruptionActions.requestCancelProtocolError,
    DisruptionActions.requestAddProtocolStepError,
    DisruptionActions.requestProtocolStepsDeleteError,
    DisruptionActions.requestProtocolStepsCompleteError,
    DisruptionActions.requestProtocolStepsCancelError,
    DisruptionActions.requestEditProtocolStepReactRoleError,
    DisruptionActions.requestEditProtocolStepManualFinishedDateError,
    DisruptionActions.requestEditProtocolStepExecuteResultError,
    DisruptionActions.requestChildDisruptionListError,
    DisruptionActions.requestTrackClosureListError,
    DisruptionActions.requestShowOnWallError,
    DisruptionActions.requestHideFromWallError,
    DisruptionActions.requestAddTrackClosureError,
    DisruptionActions.requestCompleteTrackClosureError,
    DisruptionActions.requestTagsError,
    (state, payload) => ({
      ...state,
      error: payload.error,
      status: EStateStatus.ERROR,
    }),
  ),

  on(
    DisruptionActions.requestEditTagsError,
    DisruptionActions.requestDeleteSystemTagError,
    (state, payload) => ({
      ...state,
      tags: [...state.tags],
      error: payload.error,
      status: EStateStatus.ERROR,
    }),
  ),

  on(DisruptionActions.setCountForState, (state, payload) => ({
    ...state,
    stateListWithCount: payload?.list,
  })),

  on(DisruptionActions.requestTagsSuccess, (state, { tags }) => ({
    ...state,
    tags,
    status: EStateStatus.SUCCESS,
  })),

  on(
    DisruptionActions.requestProtocolsSuccess,
    (state, { snapshot, silent }) => ({
      ...state,
      activeProtocol: snapshot.activeProtocol,
      activeProtocolActualDate: snapshot.activeProtocolActualDate,
      activeProtocolSteps: snapshot.activeProtocolSteps,
      histProtocols: [...snapshot.histProtocols].sort((a, b) =>
        dayjs(b.completionDate).diff(dayjs(a.completionDate)),
      ),
      hasChanges: silent ? state.hasChanges : false,
      status: silent ? state.status : EStateStatus.SUCCESS,
    }),
  ),

  on(DisruptionActions.moveActiveProtocolToHistory, (state) => {
    if (!state.activeProtocol) {
      return state;
    }

    const steps = state.activeProtocolSteps ?? [];
    const histProtocol: IHistDisruptionProtocol = {
      ...state.activeProtocol,
      steps,
      completedStepNumbers: steps
        .filter(
          (step) =>
            step.stateCode ===
            EInitEventDisruptionProtocolStepStatusCode.DISP_PROTOCOL_STEP_COMPLETED,
        )
        .map((step) => step.stepNumber),
    };

    return {
      ...state,
      activeProtocol: null,
      activeProtocolActualDate: null,
      activeProtocolSteps: [],
      histProtocols: [...state.histProtocols, histProtocol].sort((a, b) =>
        dayjs(b.completionDate).diff(dayjs(a.completionDate)),
      ),
    };
  }),

  on(DisruptionActions.requestProtocolsError, (state, { error, silent }) =>
    silent
      ? state
      : {
          ...state,
          error,
          status: EStateStatus.ERROR,
        },
  ),

  on(DisruptionActions.requestPvsTaskListSuccess, (state, payload) => ({
    ...state,
    pvsTaskList: payload?.pvsTaskList ?? [],
  })),
  on(DisruptionActions.requestChildDisruptionListSuccess, (state, payload) => ({
    ...state,
    childDisruptionList: payload?.childDisruptionList ?? [],
  })),
  on(DisruptionActions.requestTrackClosureListSuccess, (state, payload) => ({
    ...state,
    metroTrackClosureList: payload?.metroTrackClosureList ?? [],
  })),
  on(DisruptionActions.disruptionInfoReceived, (state, { item }) =>
    applyRemoteCardInfo(state, toDisruptionCardInfo(item)),
  ),
  on(DisruptionActions.applyPendingDisruptionInfo, (state) => {
    if (!state.pendingItem) {
      return state;
    }

    return {
      ...state,
      item: state.pendingItem,
      pendingItem: null,
      remoteInfoChanged: false,
      form: null,
      hasChanges: false,
    };
  }),
  on(DisruptionActions.setFormValues, (state, payload) => {
    const someField = Object.values(payload.form).some((v) => !!v);
    const hasChanges: boolean =
      someField && !compareObjects(payload.form, state.item, false);

    return {
      ...state,
      form: payload.form,
      hasChanges,
      status: EStateStatus.PENDING,
    };
  }),
);

function isStaleInfoResponse(
  infoRevision: number,
  observedRevision: number | undefined,
): boolean {
  return (
    typeof observedRevision === 'number' && observedRevision !== infoRevision
  );
}

function applyRemoteCardInfo(
  state: IDisruptionState,
  next: IDisruptionCardInfo,
): IDisruptionState {
  const observed: IDisruptionState = {
    ...state,
    infoRevision: state.infoRevision + 1,
  };

  if (observed.item && compareObjects(next, observed.item, false)) {
    if (!observed.remoteInfoChanged && !observed.pendingItem) {
      return observed;
    }

    return {
      ...observed,
      pendingItem: null,
      remoteInfoChanged: false,
    };
  }

  if (observed.hasChanges) {
    return {
      ...observed,
      pendingItem: next,
      remoteInfoChanged: true,
    };
  }

  return {
    ...observed,
    item: next,
    pendingItem: null,
    remoteInfoChanged: false,
  };
}
